import { useMemo } from 'react';
import { useUser, useCompany } from '../contexts';
import { isMcpConnectedForUser } from '../utils/mcpUtils';

// The three high-value connections a new account should complete after
// onboarding. Each maps to a section anchor on the Integrations page.
//
// Signal sources (all read synchronously from context, already unwrapped —
// no `.data` envelope):
//   email → company-level unipile_accounts subscription (unipileAccounts)
//   phone → user-level users/{userId}.phoneNumber
//   mcp   → per-user for OAuth connections (companies/{companyId}.mcpUsersSeen
//           keyed by this user's uid) and company-wide for the shared API-key
//           path — see isMcpConnectedForUser. So a teammate who hasn't connected
//           their own AI client still gets nudged, matching the Integrations page.
//           Deliberately NOT companyData.apiKey — that only means a key was
//           generated, not that any client ever authenticated.
export default function useSetupChecklist() {
    const { user, userData } = useUser();
    const { companyId, companyData, unipileAccounts, unipileAccountsLoading, loading: companyLoading } = useCompany();

    return useMemo(() => {
        const emailDone = Array.isArray(unipileAccounts) && unipileAccounts.length > 0;
        // Two SMS setup paths both satisfy this item:
        //   forward → users/{uid}.phoneNumber (per-user)
        //   twilio  → companies/{companyId}.smsConfig.allowedFromNumbers (company-wide)
        // The Twilio path is the Recommended option and never writes phoneNumber, so
        // gating on phoneNumber alone left it unchecked forever. Treat a configured
        // Twilio inbox as done too — like email, it's shared company infra.
        const phoneDone = !!userData?.phoneNumber
            || (Array.isArray(companyData?.smsConfig?.allowedFromNumbers)
                && companyData.smsConfig.allowedFromNumbers.length > 0);
        // user.userId is the Firebase uid (=== the key mcpUsersSeen is stored under).
        const mcpDone = isMcpConnectedForUser(companyData, user?.userId);

        const items = [
            {
                id: 'email',
                done: emailDone,
                title: 'Never miss a deal in your inbox',
                benefit: 'Auto-pull wholesaler deal emails straight into your pipeline.',
                cta: 'Connect email',
                href: '/integrations#email',
            },
            {
                id: 'phone',
                done: phoneDone,
                title: 'Get texted deals analyzed automatically',
                benefit: 'Forward SMS leads in and we underwrite them for you.',
                cta: 'Add your number',
                href: '/integrations#sms',
            },
            {
                id: 'mcp',
                done: mcpDone,
                title: 'Run Clozers from Claude, ChatGPT & Gemini',
                benefit: 'Pull deals and submit new ones just by asking.',
                cta: 'Connect AI',
                href: '/integrations#mcp',
            },
        ];

        const completedCount = items.filter((i) => i.done).length;

        return {
            items,
            completedCount,
            total: items.length,
            allDone: completedCount === items.length,
            // Don't surface the checklist until every signal is actually known.
            // The email signal (unipileAccounts) loads via a separate snapshot and
            // starts null; gating on unipileAccountsLoading too prevents both a
            // "0 of 3" flash AND a spurious "You're all set" celebration for
            // already-connected users whose email signal resolves a beat later.
            ready: !!companyId && !companyLoading && !unipileAccountsLoading,
        };
    // companyData passed whole to isMcpConnectedForUser, so depend on it directly
    // (it's replaced wholesale on every company-doc snapshot anyway).
    }, [unipileAccounts, unipileAccountsLoading, userData?.phoneNumber, user?.userId, companyData, companyId, companyLoading]);
}
